import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Eye, EyeOff } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function SignInPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, isAuthenticated } = useAuth();

    const successMessage = location.state?.message || "";

    const [formData, setFormData] = useState({
        email: "",
        password: "",
    });

    const [errors, setErrors] = useState({});
    const [apiError, setApiError] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        if (isAuthenticated) {
            navigate("/dashboard");
        }
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        if (location.state?.message) {
            window.history.replaceState({}, document.title);
        }
    }, [location]);

    const validateField = (name, value) => {
        let msg = "";

        if (name === "email") {
            if (!value) msg = "Email required";
            else if (!EMAIL_RE.test(value)) msg = "Invalid email";
        }

        if (name === "password") {
            if (!value) msg = "Password required";
        }

        setErrors((p) => ({ ...p, [name]: msg }));
        return msg;
    };

    const handleChange = (name, value) => {
        if (name === "email") {
            value = value.replace(/\s/g, "");
        }

        setFormData((p) => ({ ...p, [name]: value }));
        validateField(name, value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setApiError("");

        const emailErr = validateField("email", formData.email);
        const passErr = validateField("password", formData.password);

        if (emailErr || passErr) return;

        setLoading(true);

        try {
            const user = await login(formData.email, formData.password);

            if (user) {
                navigate("/dashboard");
            }
        } catch (err) {
            console.error(err);

            if (err.code === "auth/user-not-found") {
                setApiError("User not found. Please sign up.");
            } else if (err.code === "auth/wrong-password") {
                setApiError("Incorrect password.");
            } else if (err.code === "auth/invalid-email") {
                setApiError("Invalid email.");
            } else {
                setApiError("Login failed. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.page}>
            <div style={styles.card}>
                <h1 style={styles.title}>Welcome Back</h1>
                <p style={styles.subtitle}>Sign in to continue</p>

                {/* SUCCESS MESSAGE */}
                {successMessage && (
                    <div style={styles.success}>{successMessage}</div>
                )}

                {/* ERROR */}
                {apiError && <p style={styles.error}>{apiError}</p>}

                <form onSubmit={handleSubmit} style={styles.form}>
                    {/* EMAIL */}
                    <div style={styles.inputGroup}>
                        <input
                            placeholder="Email *"
                            value={formData.email}
                            onChange={(e) =>
                                handleChange("email", e.target.value)
                            }
                            style={styles.input}
                        />
                        {errors.email && <p style={styles.error}>{errors.email}</p>}
                    </div>

                    {/* PASSWORD */}
                    <div style={styles.inputGroup}>
                        <div style={styles.passwordWrapper}>
                            <input
                                type={showPassword ? "text" : "password"}
                                placeholder="Password *"
                                value={formData.password}
                                onChange={(e) =>
                                    handleChange("password", e.target.value)
                                }
                                style={styles.passwordInput}
                            />

                            <div style={styles.iconGroup}>
                                <span
                                    style={styles.icon}
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </span>
                            </div>
                        </div>
                        {errors.password && (
                            <p style={styles.error}>{errors.password}</p>
                        )}
                    </div>

                    {/* BUTTON */}
                    <button
                        style={{
                            ...styles.button,
                            ...(loading ? styles.buttonDisabled : {}),
                        }}
                        disabled={loading}
                    >
                        {loading ? "Signing In..." : "Sign In"}
                    </button>
                </form>

                <p style={styles.footer}>
                    Don't have an account?{" "}
                    <Link to="/signup" style={styles.link}>
                        Sign Up
                    </Link>
                </p>
            </div>
        </div>
    );
}

/* ── STYLES ── */
const styles = {
    page: {
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background:
            "linear-gradient(111.1deg, rgb(0,40,70) -4.8%, rgb(255,229,115) 82.7%, rgb(255,203,15) 97.2%)",
    },

    card: {
        width: "100%",
        maxWidth: "400px",
        padding: "30px",
        borderRadius: "20px",
        background: "rgba(0,40,70,0.85)",
        border: "1px solid rgba(0,255,150,0.3)",
        backdropFilter: "blur(10px)",
        color: "#fff",
    },

    title: {
        fontSize: "28px",
        fontWeight: "bold",
        textAlign: "center",
    },

    subtitle: {
        textAlign: "center",
        color: "#ccc",
        marginBottom: "20px",
    },

    success: {
        background: "rgba(0,255,150,0.1)",
        border: "1px solid rgba(0,255,150,0.3)",
        color: "#00f5a0",
        padding: "10px",
        borderRadius: "10px",
        fontSize: "13px",
        marginBottom: "10px",
        textAlign: "center",
    },

    form: {
        display: "flex",
        flexDirection: "column",
        gap: "15px",
    },

    inputGroup: {
        display: "flex",
        flexDirection: "column",
    },

    input: {
        padding: "12px",
        borderRadius: "10px",
        border: "1px solid rgba(0,255,150,0.3)",
        background: "rgba(255,255,255,0.05)",
        color: "#fff",
        outline: "none",
    },

    passwordWrapper: {
        position: "relative",
        width: "100%",
    },

    passwordInput: {
        width: "100%",
        padding: "12px 40px 12px 12px",
        borderRadius: "10px",
        border: "1px solid rgba(0,255,150,0.3)",
        background: "rgba(255,255,255,0.05)",
        color: "#fff",
        outline: "none",
        boxSizing: "border-box",
    },


    iconGroup: {
        position: "absolute",
        right: "10px",
        top: "50%",
        transform: "translateY(-50%)",
    },

    icon: {
        cursor: "pointer",
        color: "#aaa",
    },

    error: {
        color: "#ff6b6b",
        fontSize: "12px",
        marginTop: "4px",
    },

    button: {
        marginTop: "10px",
        padding: "12px",
        borderRadius: "25px",
        border: "none",
        fontWeight: "bold",
        background: "linear-gradient(90deg, #00f5a0, #00d9f5)",
        cursor: "pointer",
        transition: "all 0.3s ease",
    },

    buttonDisabled: {
        opacity: 0.6,
        cursor: "not-allowed",
    },

    footer: {
        textAlign: "center",
        marginTop: "20px",
    },

    link: {
        color: "#00f5a0",
        textDecoration: "none",
        fontWeight: "bold",
    },
};

export default SignInPage;